import { Callout } from '@humanforce/mango-ui-react';
import type { WorkerPayrollDetails } from '../workerDirectoryApi';
import { DetailPair, SectionShell } from './SectionShell';

const shown = (value: string | number | null | undefined): string =>
  value === null || value === undefined || String(value).trim() === '' ? 'Not supplied' : String(value);
const answer = (value: boolean | null): string => value === null ? 'Unknown' : value ? 'Yes' : 'No';
const knownChoice = (value: string | null): string => value?.trim() || 'Unknown';
const date = (value: string | null): string => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Unknown';
  const [year, month, dateNumber] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, dateNumber));
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : 'Unknown';
};
const grid = 'mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l';

export const HrBanking = ({ banking }: { banking: WorkerPayrollDetails['banking'] }) => (
  <SectionShell title="Banking" description="Read-only details from Humanforce HR.">
    {!banking?.accounts.length ? <Callout tone="neutral" title="No banking details supplied by HR" />
      : <div className="mgo-stack mgo-gap-l">{banking.accounts.map((account, index) => (
        <section key={index} aria-label={`Account ${index + 1}`} className="mgo-stack mgo-gap-s">
          <h4 className="mgo-text-s mgo-weight-strong">Account {index + 1}</h4>
          <div className={grid}>
            <DetailPair label="Account name" value={shown(account.accountName)} />
            <DetailPair label="BSB" value={shown(account.bsb)} />
            <DetailPair label="Account number" value={shown(account.maskedAccountNumber)} />
            <DetailPair label="Allocation kind" value={shown(account.splitKind)} />
            <DetailPair label="Allocation value" value={shown(account.splitValue)} />
            <DetailPair label="Source rank" value={shown(account.rank)} />
          </div>
        </section>
      ))}</div>}
  </SectionShell>
);

export const HrTax = ({ tax }: { tax: WorkerPayrollDetails['tax'] }) => {
  if (!tax) return <SectionShell title="Tax" description="Read-only details from Humanforce HR.">
    <Callout tone="neutral" title="No tax declaration supplied by HR" />
  </SectionShell>;
  const d = tax.declaration;
  return <div className="mgo-stack mgo-gap-l">
    <SectionShell title="Tax choices" description="Read-only declaration from Humanforce HR."><div className={grid}>
      <DetailPair label="Tax residency" value={knownChoice(tax.residencyType)} />
      <DetailPair label="Tax-free threshold claimed" value={answer(tax.taxFreeThresholdClaimed)} />
      <DetailPair label="Pay basis" value={knownChoice(tax.payBasis)} />
      <DetailPair label="Study/training loan repayment" value={answer(tax.hasStudyAndTrainingRepayment)} />
      <DetailPair label="Financial supplement debt" value={answer(tax.hasFinancialSupplementDebt)} />
    </div></SectionShell>
    <SectionShell title="TFN and exemption"><div className={grid}>
      <DetailPair label="TFN supplied" value={answer(tax.tfnSupplied)} />
      <DetailPair label="TFN exemption" value={knownChoice(tax.tfnExemptionType)} />
    </div></SectionShell>
    <SectionShell title="Declaration identity and address"><div className={grid}>
      <DetailPair label="Title" value={shown(d.title)} />
      <DetailPair label="Given name" value={shown(d.givenName)} />
      <DetailPair label="Middle name" value={shown(d.middleName)} />
      <DetailPair label="Family name" value={shown(d.familyName)} />
      <DetailPair label="Declared date of birth" value={date(d.dateOfBirth)} />
      <DetailPair label="Address line 1" value={shown(d.addressLine1)} />
      {d.addressLine2 && <DetailPair label="Address line 2" value={d.addressLine2} />}
      <DetailPair label="Suburb/locality" value={shown(d.locality)} />
      <DetailPair label="State" value={shown(d.state)} />
      <DetailPair label="Postcode" value={shown(d.postcode)} />
    </div></SectionShell>
    {(d.previousGivenName || d.previousFamilyName) && <SectionShell title="Previous names"><div className={grid}>
      {d.previousGivenName && <DetailPair label="Previous given name" value={d.previousGivenName} />}
      {d.previousFamilyName && <DetailPair label="Previous family name" value={d.previousFamilyName} />}
    </div></SectionShell>}
    <SectionShell title="Origin"><div className={grid}>
      <DetailPair label="Country of origin" value={shown(tax.origin.name)} />
    </div></SectionShell>
    <SectionShell title="Acceptance"><div className={grid}>
      <DetailPair label="Declaration information accepted" value={answer(tax.acceptance.information)} />
      <DetailPair label="Stapling accepted" value={answer(tax.acceptance.stapling)} />
      <DetailPair label="TFN declaration accepted" value={answer(tax.acceptance.tfnd)} />
    </div></SectionShell>
  </div>;
};

export const HrSuperFund = ({ superFund }: { superFund: WorkerPayrollDetails['superFund'] }) => (
  <SectionShell title="Super Fund Details" description="Fund selection from Humanforce HR. Read-only.">
    {!superFund ? <Callout tone="neutral" title="No super fund selection supplied by HR" />
      : superFund.variant === 'ambiguous' ? <Callout tone="caution" title="Super fund selection unavailable">HR returned an ambiguous fund selection.</Callout>
      : <div className={grid}>
        <DetailPair label={superFund.variant === 'smsf' ? 'SMSF fund name' : 'Fund name'} value={shown(superFund.fundName)} />
        <DetailPair label={superFund.variant === 'smsf' ? 'SMSF ABN' : 'Fund ABN'} value={shown(superFund.fundAbn)} />
        {superFund.variant === 'product' && <DetailPair label="USI" value={shown(superFund.usi)} />}
        {superFund.variant === 'smsf' && <DetailPair label="Electronic service address" value={shown(superFund.esa)} />}
        <DetailPair label="Member number" value={shown(superFund.maskedMemberNumber)} />
      </div>}
  </SectionShell>
);
